import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/api/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PackageOpen, Plus, X, ArrowRight, User, Truck, FileDown, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SignaturePad from "@/components/SignaturePad";
import PageShell from "@/components/layout/PageShell";
import ServerPagination from "@/components/ServerPagination";
import LogReportDialog from "@/components/LogReportDialog";
import LogItemsEditor from "@/components/LogItemsEditor";
import ItemsReportDialog from "@/components/ItemsReportDialog";
import { ClipboardList } from "lucide-react";

const EMPTY = { employee_name: "", phone: "", department: "", drop_type: "own", signature_data_url: "", notes: "", item_type: "Parcel", parcel_condition: "Good", items: [] };
const LSKEY = "olog.pageSize";

export default function OutwardLogPage() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(parseInt(localStorage.getItem(LSKEY) || "50", 10));
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [saveAndNew, setSaveAndNew] = useState(false);
  const firstFieldRef = useRef(null);
  const [reportLog, setReportLog] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [manifestLog, setManifestLog] = useState(null);

  const openEdit = (l) => {
    setEditingId(l.id);
    setForm({
      ...EMPTY,
      employee_name: l.employee_name || "",
      phone: l.phone || "",
      department: l.department || "",
      drop_type: l.drop_type || "own",
      signature_data_url: l.signature_data_url || "",
      notes: l.notes || "",
    });
    setOpen(true);
  };

  const load = async (nextPage = page, nextSize = pageSize) => {
    try {
      const { data } = await api.get("/outward-logs", { params: { page: nextPage, page_size: nextSize } });
      setItems(data.items || []);
      setTotal(data.total || 0); setPages(data.pages || 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { load(1, pageSize); /* eslint-disable-next-line */ }, []);
  useEffect(() => { load(page, pageSize); /* eslint-disable-next-line */ }, [page, pageSize]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.employee_name) return toast.error("Employee name required");
    if (!editingId && !form.signature_data_url) return toast.error("Please sign to confirm handoff");
    setBusy(true);
    try {
      if (editingId) {
        await api.patch(`/outward-logs/${editingId}`, {
          employee_name: form.employee_name, phone: form.phone,
          department: form.department, drop_type: form.drop_type, notes: form.notes,
        });
        toast.success("Outward log updated");
        setOpen(false); setEditingId(null); setForm(EMPTY); await load();
        return;
      }
      const { data } = await api.post("/outward-logs", form);
      toast.success("Outward log saved");
      if (saveAndNew) {
        setSaveAndNew(false);
        setForm(EMPTY);
        await load();
        setTimeout(() => firstFieldRef.current?.focus?.(), 30);
        return;
      }
      setOpen(false); setForm(EMPTY); await load();
      // Route based on drop type: Pickup → Pickups feature · Own → Outward Entry
      if (form.drop_type === "pickup") nav(`/app/pickups?log_id=${data.id}&prefill_name=${encodeURIComponent(form.employee_name)}&prefill_phone=${encodeURIComponent(form.phone || "")}`);
      else nav(`/app/outward?log_id=${data.id}&sender_name=${encodeURIComponent(form.employee_name)}&sender_phone=${encodeURIComponent(form.phone || "")}&dept=${encodeURIComponent(form.department || "")}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <PageShell
      title="Outward Log."
      overline={<><PackageOpen size={12}/> Outward · consignor log</>}
      subtitle={"Log the employee before you dispatch. Chooses between \"Dropped by own\" → Outward Entry, or \"Pickup\" → Pickup feature."}
      testid="olog"
      actions={
        <div className="flex items-center gap-2">
          <Button data-testid="olog-report" onClick={() => setReportOpen(true)} variant="outline"
                  className="rounded-md border-primary text-primary hover:bg-primary hover:text-white">
            <FileDown size={14} className="mr-2" /> Report
          </Button>
          <Button data-testid="olog-new" onClick={() => setOpen(true)} className="rounded-md bg-primary text-primary-foreground shadow-brut-orange">
            <Plus size={14} className="mr-2" /> New log entry
          </Button>
        </div>
      }
    >

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border overline text-left">
              <th className="p-4">When</th><th className="p-4">Employee</th><th className="p-4">Phone</th>
              <th className="p-4">Department</th><th className="p-4">Drop type</th><th className="p-4">Status</th><th className="p-4 text-right"></th>
            </tr>
          </thead>
          <tbody data-testid="olog-table">
            {items.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-muted-foreground text-sm">No log entries yet. Log an outbound consignor when they arrive.</td></tr>
            )}
            {items.map((l) => (
              <tr key={l.id} className="border-b border-border hover:bg-muted/40" data-testid={`olog-row-${l.id}`}>
                <td className="p-4 mono text-xs">{(l.created_at || "").slice(0,16).replace("T"," ")}</td>
                <td className="p-4 font-semibold">{l.employee_name}</td>
                <td className="p-4 mono text-xs">{l.phone || "—"}</td>
                <td className="p-4 text-xs">{l.department || "—"}</td>
                <td className="p-4">
                  <span className={`text-[10px] uppercase tracking-widest font-bold border px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${
                    l.drop_type === "pickup" ? "border-primary text-primary bg-red-50" : "border-emerald-500 text-emerald-800 bg-emerald-50"
                  }`}>
                    {l.drop_type === "pickup" ? <><Truck size={10}/> Pickup</> : <><User size={10}/> Dropped by own</>}
                  </span>
                </td>
                <td className="p-4 text-xs">{l.status || "open"}</td>
                <td className="p-4 text-right">
                  <Button size="sm" variant="outline" onClick={() => {
                    if (l.drop_type === "pickup") nav(`/app/pickups?log_id=${l.id}`);
                    else nav(`/app/outward?log_id=${l.id}&sender_name=${encodeURIComponent(l.employee_name)}`);
                  }} data-testid={`olog-open-${l.id}`} className="rounded-md">
                    Continue <ArrowRight size={12} className="ml-1" />
                  </Button>
                  <button type="button" onClick={() => openEdit(l)} data-testid={`olog-edit-${l.id}`}
                    className="ml-2 text-muted-foreground hover:text-primary p-1 rounded transition-colors inline-flex align-middle" title="Edit log">
                    <Pencil size={13} />
                  </button>
                  <button type="button" onClick={() => setManifestLog(l)} data-testid={`olog-manifest-${l.id}`}
                    className="ml-1 text-muted-foreground hover:text-primary p-1 rounded transition-colors inline-flex align-middle" title="Manifest">
                    <FileDown size={13} />
                  </button>
                  <button type="button" onClick={() => setReportLog(l)} data-testid={`olog-items-${l.id}`}
                    className="ml-1 text-muted-foreground hover:text-primary p-1 rounded transition-colors inline-flex align-middle" title="Items report">
                    <ClipboardList size={13} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ServerPagination
        page={page} pageSize={pageSize} total={total} pages={pages}
        onPageChange={(n) => setPage(n)}
        onPageSizeChange={(s) => { setPageSize(s); localStorage.setItem(LSKEY, String(s)); setPage(1); }}
        storageKey={LSKEY} testidPrefix="olog-pg"
      />

      <LogReportDialog open={reportOpen} onClose={() => setReportOpen(false)} direction="outward" />
      <LogReportDialog open={!!manifestLog} onClose={() => setManifestLog(null)} direction="outward" logId={manifestLog?.id} manifestMode={true} />

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(EMPTY); } }}>
        <DialogContent className="rounded-md max-w-lg p-0 flex flex-col max-h-[85vh] gap-0" data-testid="olog-create-dialog">
          <form onSubmit={save} className="flex flex-col flex-1 min-h-0">
            <DialogHeader className="px-6 pt-6 pb-3 border-b border-border shrink-0">
              <DialogTitle className="font-display text-2xl">{editingId ? "Edit outward log entry" : "New outward log entry"}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3 min-h-0 pb-8">
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="overline">Employee name *</span>
                  <Input data-testid="olog-emp" ref={firstFieldRef} value={form.employee_name} onChange={e => setForm({...form, employee_name: e.target.value})} required className="mt-1 rounded-md" />
                </label>
                <label className="block">
                  <span className="overline">Phone</span>
                  <Input data-testid="olog-phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="mt-1 rounded-md font-mono" />
                </label>
              </div>
              <label className="block">
                <span className="overline">Department</span>
                <Input data-testid="olog-dept" value={form.department} onChange={e => setForm({...form, department: e.target.value})} className="mt-1 rounded-md" placeholder="e.g. Finance" />
              </label>

              <div>
                <span className="overline">Drop type *</span>
                <div className="mt-1 grid grid-cols-2 gap-3">
                  {[
                    { v: "own", label: "Dropped by own", desc: "Employee walked to the mailroom", Icon: User },
                    { v: "pickup", label: "Pickup", desc: "Need a runner to collect", Icon: Truck },
                  ].map(({ v, label, desc, Icon }) => {
                    const active = form.drop_type === v;
                    return (
                      <label key={v} data-testid={`olog-drop-${v}`} className={`flex items-start gap-3 border rounded-md p-3 cursor-pointer transition-colors ${
                        active ? "border-primary bg-red-50 shadow-brut-orange" : "border-border hover:border-primary/40"
                      }`}>
                        <input type="radio" checked={active} onChange={() => setForm({...form, drop_type: v})} className="mt-1 accent-[#BE1E1E]" />
                        <div className="flex-1">
                          <div className="font-semibold text-sm flex items-center gap-1.5"><Icon size={12}/> {label}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">{desc}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              <label className="block">
                <span className="overline">Notes</span>
                <Input data-testid="olog-notes" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="mt-1 rounded-md" placeholder="Optional" />
              </label>
              {form.drop_type === "own" && (
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="overline">Item type</span>
                    <select data-testid="olog-item-type" value={form.item_type} onChange={e => setForm({...form, item_type: e.target.value})}
                      className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                      {["Parcel","Document","Letter","Box","Package"].map(t => <option key={t}>{t}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="overline">Parcel condition (from employee)</span>
                    <select data-testid="olog-condition" value={form.parcel_condition} onChange={e => setForm({...form, parcel_condition: e.target.value})}
                      className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                      {["Excellent","Good","Damaged","Tampered","Wet","Open"].map(c => <option key={c}>{c}</option>)}
                    </select>
                  </label>
                </div>
              )}
              {form.drop_type === "own" && (
                <LogItemsEditor value={form.items || []} onChange={(items) => setForm({...form, items})} testid="olog-items" variant="out" />
              )}

              <div>
                <span className="overline">Consignor signature *</span>
                <div className="mt-1"><SignaturePad value={form.signature_data_url} onChange={v => setForm({...form, signature_data_url: v})} testid="olog-signature" /></div>
              </div>
            </div>

            <div className="shrink-0 px-6 py-3 bg-white border-t border-border flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => { setOpen(false); setForm(EMPTY); }} data-testid="olog-cancel" className="rounded-md"><X size={13} className="mr-1"/> Cancel</Button>
              <Button type="submit" onClick={() => setSaveAndNew(true)} disabled={busy} data-testid="olog-save-new" variant="outline" className="rounded-md border-primary text-primary hover:bg-primary hover:text-white">
                <Plus size={13} className="mr-1"/> Save &amp; New
              </Button>
              <Button type="submit" onClick={() => setSaveAndNew(false)} disabled={busy} data-testid="olog-submit" className="rounded-md bg-primary text-primary-foreground">
                {busy ? "Saving…" : (form.drop_type === "pickup" ? "Save & go to Pickup" : "Save & go to Outward Entry")} <ArrowRight size={13} className="ml-1" />
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      <ItemsReportDialog log={reportLog} open={!!reportLog} onClose={() => setReportLog(null)} />
    </PageShell>
  );
}
